import { AddBox, Delete, Edit } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel, MenuItem, Modal, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import axiosInstance from "../../../utils/axiosInstance";

const Achievements = () => {
    const [achievements, setAchievements] = useState([
        {
            achievement_id: '',
            participant_id: '',
            title: '',
            description: '',
            difficulty: '',
            points: '',
            img_url: '',
            createdAt: ''
        }
    ]);
    const getAchievementsRef = useRef()

    const [achievement, setAchievement] = useState({
        title: '',
        description: '',
        difficulty: '',
        participant_id: '',
        points: '',
        img_url: ''
    })
    const [participants, setParticipants] = useState([
        {
            participant_id: '',
            achievement_id: '',
            name: '',
            email: '',
            phone: '',
            role: ''
        }
    ])
    const [selectedAchievement, setSelectedAchievement] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const filteredAchievements = achievements.filter((achievement) =>
        achievement.title.toLowerCase().includes(searchQuery.toLowerCase()) || achievement.participant_id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const paginatedAchievements = filteredAchievements?.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Delete confirmation handlers
    const handleOpenDeleteModal = (achievement) => {
        setIsDeleteModalOpen(true);
        setSelectedAchievement(achievement)
    };

    const handleOpenEditModal = (achievement) => {
        setIsEditModalOpen(true);
        setSelectedAchievement(achievement)
    };        

    const handleAddAchievement = async () => {
        await axiosInstance.post(`achievements/create`, achievement, {
                headers: { 'Content-Type': 'application/json' }
            }).then(() => {
            toast.success(`Success, successfully added ${achievement.title}`)
            setTimeout(() => {
                if(getAchievementsRef.current) getAchievementsRef.current();
                setIsAddModalOpen(false);
            }, 2000)
        }).catch((error) => {
            if(error.status === 404) {
                toast.error('Failure! Achievements not found.')
            } else if(error.status === 400) {
                toast.error('Failure! Achievement can not be edited')
            } else if(error.status === 500) {
                toast.error('Failure! Network connection unstable, try again later.')
            }
        })
    }

    const handleEditAchievement = async () => {
        await axiosInstance.put(`achievements/update/${selectedAchievement.achievement_id}`, selectedAchievement).then(() => {
            toast.success(`Success, successfully edited ${selectedAchievement.title}`)
            setTimeout(() => {
                setSelectedAchievement({})
                if(getAchievementsRef.current) getAchievementsRef.current();
                setIsEditModalOpen(false)
            }, 2000)
        }).catch((error) => {
            if(error.status === 404) {
                toast.error(`Failure, guest not found`)
            } else if(error.status === 400) {
                toast.error(`Failure, ${error.response.error}`)
            } else {
                toast.error(`Failure, ${error}`)
            }
        })
    }

    const handleDeleteAchievement = async () => {
        await axiosInstance.delete(`achievements/delete/${selectedAchievement.achievement_id}`).then(() => {
            toast.success(`Success, successfully deleted ${selectedAchievement.title}`)
            setTimeout(() => {
                setSelectedAchievement({})
                if(getAchievementsRef.current) getAchievementsRef.current();
                setIsDeleteModalOpen(false)
            }, 2000)
        }).catch((error) => {
            if(error.status === 404) {
                toast.error("Achievement not found")
            } else if(error.status === 400) {
                toast.error(error.response.error)
            } else {
                toast.error(error)
            }
        })
    }

    const getParticipantName = (id) => {
        return participants.find((participant) => participant.guest_id || participant.member_id === id)?.name || '';
    }

    useEffect(() => {
        const getParticipants = async () => {
            await axiosInstance.get(`auth/all_users`).then((response) => {
                setParticipants(response.data.users)
            }).catch(() => {
                setParticipants([])
            })
        }

        const getAchievements = async () => {
            await axiosInstance.get(`achievements/all`).then((result) => {
                setAchievements(result.data.achievements);
            }).catch(() => {
                setAchievements([])
            })
        }

        getAchievementsRef.current = getAchievements;

        getAchievements();
        getParticipants()
    }, [])

    return(
        <div>
            <div className="md:flex justify-between md:flex-wrap">
                <Typography variant='h4' className="text-blue-800">Manage achievements</Typography>
                <div className="mt-4 md:mt-0"><Button className="flex gap-3" variant="outlined" onClick={() => setIsAddModalOpen(true)}><AddBox /> Add achievement</Button></div>
            </div>
            <br />

            <div className="p-4">
                <TextField label="Search for achievements by titel, email" variant="outlined" fullWidth value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                <TableContainer component={Paper} className="shadow mt-4" sx={{ color: "#1f2937" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Participant</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Difficulty</TableCell>
                                <TableCell>Achieved on</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedAchievements.map((achievement, index) => (
                                <TableRow key={index}>
                                    <TableCell>{index+1}</TableCell>
                                    <TableCell>{achievement.title}</TableCell>
                                    <TableCell>{getParticipantName(achievement.participant_id) || <i>Not found</i>}</TableCell>
                                    <TableCell>{achievement.description}</TableCell>
                                    <TableCell>{achievement.difficulty}</TableCell>
                                    <TableCell>{achievement?.createdAt?.slice(0,10)}</TableCell>
                                    <TableCell className="flex gap-2">
                                        {/* <RemoveRedEye color="success" className="cursor-pointer" /> */}
                                        <Edit color="primary" onClick={() => handleOpenEditModal(achievement)} className="cursor-pointer" />
                                        <Delete color="error" onClick={() => handleOpenDeleteModal(achievement)} className="cursor-pointer" />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredAchievements.length === 0 && <TableRow><TableCell colSpan={7} className="text-center">No achievement found.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, filteredAchievements.length]}
                        component="div"
                        count={filteredAchievements.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            </div>

            {/* Delete Confirmation Modal */}
            <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                <DialogTitle className="text-blue-950">Delete Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this booking
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteModalOpen(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteAchievement} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit booking Modal */}
            <Modal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} className="flex justify-center items-center" aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <div className='bg-white w-[90%] lg:w-[40%] shadow p-8 rounded-sm '>
                    <h2 className="text-blue-950 mb-8">Edit achievement</h2>
                    <div>
                        <TextField variant="outlined" label="Enter title" fullWidth type="text" value={selectedAchievement.title} onChange={(e) => setSelectedAchievement((prev) => ({...prev, title: e.target.value}))} style={{ marginBottom: 10 }} />
                        <FormControl fullWidth style={{ marginBottom: 15 }}>
                            <InputLabel>Participants</InputLabel>
                            <Select label="Select participant" value={selectedAchievement.participant_id} onChange={(e) => setSelectedAchievement((prev) => ({...prev, participant_id: e.target.value}))}>
                                {participants.map((participant, index) =>
                                    <MenuItem key={index} value={participant.participant_id}>
                                        {participant.name} - {participant.role}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <TextField variant="outlined" multiline label="Enter decription" fullWidth type="text" value={selectedAchievement.description} onChange={(e) => setSelectedAchievement((prev) => ({...prev, description: e.target.value}))} style={{ marginBottom: 10 }} />
                        <FormControl fullWidth style={{ marginBottom: 15 }}>
                            <InputLabel>Difficulty</InputLabel>
                            <Select label="Select client" value={selectedAchievement.difficulty} onChange={(e) => setSelectedAchievement((prev) => ({...prev, difficulty: e.target.value}))}>
                                <MenuItem value="easy">
                                    Easy
                                </MenuItem>
                                <MenuItem value="medium">
                                    Medium
                                </MenuItem>
                                <MenuItem value="hard">
                                    Hard
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className="flex items-center justify-center gap-8 w-[90%] mx-auto mt-4">
                        <Button onClick={() => setIsEditModalOpen(false)} color="secondary" fullWidth variant="outlined">
                            Cancel
                        </Button>
                        <Button color="primary" fullWidth variant="outlined" onClick={handleEditAchievement}>
                            Save
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Add Member Modal */}
            <Modal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} className="flex justify-center items-center" aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <div className='bg-white w-[90%] lg:w-[40%] shadow p-8 rounded-sm'>
                    <h2 className="text-blue-950 mb-6">Add achievements</h2>
                    <div>
                        <TextField variant="outlined" label="Enter title" fullWidth type="text" value={achievement.title} onChange={(e) => setAchievement((prev) => ({...prev, title: e.target.value}))} style={{ marginBottom: 10 }} />
                        <FormControl fullWidth style={{ marginBottom: 15 }}>
                            <InputLabel>Participants</InputLabel>
                            <Select label="Select client" value={achievement.participant_id} onChange={(e) => setAchievement((prev) => ({...prev, participant_id: e.target.value}))}>
                                {participants.map((participant, index) =>
                                    <MenuItem key={index} value={participant.participant_id}>
                                        {participant.name} - <span className="text-blue-700 ml-2">{participant.role.toUpperCase()}</span>
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <TextField variant="outlined" multiline label="Enter decription" fullWidth type="text" value={achievement.description} onChange={(e) => setAchievement((prev) => ({...prev, description: e.target.value}))} style={{ marginBottom: 10 }} />
                        <FormControl fullWidth style={{ marginBottom: 15 }}>
                            <InputLabel>Difficulty</InputLabel>
                            <Select label="Select client" value={achievement.difficulty} onChange={(e) => setAchievement((prev) => ({...prev, difficulty: e.target.value}))}>
                                <MenuItem value="easy">
                                    Easy
                                </MenuItem>
                                <MenuItem value="medium">
                                    Medium
                                </MenuItem>
                                <MenuItem value="hard">
                                    Hard
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className="flex items-center justify-center gap-8 w-[90%] mx-auto mt-4">
                        <Button onClick={() => setIsAddModalOpen(false)} color="secondary" variant="outlined" fullWidth>
                            Cancel
                        </Button>
                        <Button color="primary" variant="outlined" fullWidth onClick={handleAddAchievement}>
                            Save
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    )
}

export default Achievements